import { useState } from "react"
import type { ReactNode } from "react"
import { Button, Heading, KeyValueList, Text } from "@smooth/design-system-components"
import { evaluationError } from "../errors"
import { dateLabel, observationLabel, observedTerminal, terminalMatch } from "../types"
import type { EvaluationRun, RunDetail, Terminals } from "../types"
import { Bubble } from "./Conversation"
import { DecisionRecord } from "./DecisionRecord"
import { usePromptVersionLabel } from "./PromptPicker"
import { AlertMessage, Disclosure, MultilineText, StatusMessage, TerminalName } from "./parts"

type Props = {
  run: EvaluationRun
  terminals: Terminals
  onInspect: (runId: number) => Promise<RunDetail>
  /** AIドラフトと見比べるもの（お手本の文） */
  reference?: ReactNode
  onAdopt?: (body: string) => void
}

/** 1回の生成結果。AIが選んだ対応と一致・不一致、ドラフト本文（または人手対応理由）、AIの判断、生成時のデータ・プロンプト（開いたときに読む） */
export function RunResult({ run, terminals, onInspect, reference, onAdopt }: Props) {
  const [detail, setDetail] = useState<RunDetail | null>(null)
  const promptLabel = usePromptVersionLabel()
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const invoke = async (action: () => Promise<void>) => {
    setBusy(true)
    setError(null)
    try {
      await action()
    } catch (e) {
      setError(evaluationError(e))
    } finally {
      setBusy(false)
    }
  }
  const observed = observedTerminal(run)
  const match = terminalMatch(run)
  const mismatch = match === "mismatch" || match === "none"
  const metadata = run.generationMetadata

  return (
    <div className="flex flex-col gap-3">
      <Text size="sm">
        {dateLabel(run.createdAt)} · {promptLabel(run.promptVersionId)}
      </Text>
      <KeyValueList
        items={[
          {
            key: "AIが選んだ対応",
            value: observed ? (
              <TerminalName id={observed} terminals={terminals} mismatch={mismatch} />
            ) : (
              "記録なし"
            ),
          },
          {
            key: "結果",
            value: (
              <Text as="span" weight="bold" tone={mismatch ? "error" : "default"}>
                {observationLabel(run)}
              </Text>
            ),
          },
        ]}
      />
      {run.status === "running" && (
        <StatusMessage>生成中です。完了すると自動で表示します。</StatusMessage>
      )}
      {run.errorMessage && <AlertMessage>{run.errorMessage}</AlertMessage>}
      {metadata?.humanReviewRequired ? (
        <>
          <div className="flex flex-col gap-2 py-3">
            <Heading level={3}>人手対応・顧客向け本文なし</Heading>
            <MultilineText>{metadata.humanReviewReason}</MultilineText>
          </div>
          {reference}
        </>
      ) : run.body ? (
        <div className="@container">
          <div className="grid gap-3 @[640px]:grid-cols-2">
            <div className="flex min-w-0 flex-col gap-2">
              <Bubble direction="outbound" meta="AIドラフト（未送信）" draft>
                {run.body}
              </Bubble>
              {onAdopt && run.status === "completed" && (
                <Button
                  variant="outline"
                  className="!h-9 self-end !px-4"
                  onClick={() => onAdopt(run.body!)}
                >
                  この下書きをお手本にする
                </Button>
              )}
            </div>
            {reference}
          </div>
        </div>
      ) : (
        reference
      )}
      {run.status === "completed" && (
        <details className="border-t border-border-default">
          <summary className="cursor-pointer py-2 text-body font-bold text-text-default">
            AIの判断
          </summary>
          <DecisionRecord metadata={metadata} />
        </details>
      )}
      <details
        className="border-t border-border-default"
        onToggle={(event) => {
          // 開いたときに初めて読む（生成時のケース全体とプロンプトは大きいので一覧では取らない）
          if (event.currentTarget.open && !detail && !busy)
            void invoke(async () => {
              setDetail(await onInspect(run.id))
            })
        }}
      >
        <summary className="cursor-pointer py-2 text-body font-bold text-text-default">
          生成時のデータ・プロンプト
        </summary>
        {detail ? (
          <div className="pl-3">
            <Disclosure summary="生成時のケースデータ">
              {JSON.stringify(detail.caseSnapshot, null, 2)}
            </Disclosure>
            <Disclosure summary="AIに渡したケース情報">{detail.userPrompt}</Disclosure>
            <Disclosure summary="固定ポリシーを含むシステムプロンプト">
              {detail.systemPrompt}
            </Disclosure>
          </div>
        ) : (
          busy && <StatusMessage>読み込んでいます…</StatusMessage>
        )}
      </details>
      {error && <AlertMessage>{error}</AlertMessage>}
    </div>
  )
}
